import debounce from 'just-debounce-it';
import type { IArticleConfig } from '~/config/article';
import widgetsConfig from '~/config/widgets.config';
// import { debugLog } from '~/debug';
import CommunicationCreator from '~/modules/CommunicationCreator';
import ServerLogger from '~/modules/ServerLogger';
import TokenHandler from '~/modules/TokenHandler';
import { advancedKeys, commentBubbleExcludeKeys } from '~/config/keys';
import Iframe from './Iframe';
import Session from '~/modules/Session';

// Typescript typings

export interface ICommentsInterface {
  commentCount: number;
}

class Comments extends Iframe {
  constructor(
    id: string,
    private readonly config: IArticleConfig,
    private readonly articleIndex: string = ''
  ) {
    const searchParams = new URLSearchParams(window.location.search);
    /** ‼ ⚠ Call parent constructor (Iframe class) */
    super(
      id,
      'Vuukle Comments Widget',
      widgetsConfig.urls.comments,
      {
        amp: config.amp,
        sdk: config.sdk,
        apiKey: config.apiKey,
        host: config.host,

        articleId: config.articleId,
        author: config.author || undefined,
        globalLang: config.language,
        img: config.img,
        lang: config.comments && config.comments.transliteration.language,
        tags: config.tags,
        title: config.title,
        url: config.url,
        referrer: config.refHost,
        font: config.theme && config.theme.font,

        // tslint:disable object-literal-sort-keys
        // Emotes
        emotesEnabled: (config.emotes && config.emotes.enabled) || true,

        refHost: config.refHost,
        l_d: config.comments && config.comments.transliteration.enabledByDefault,
        totWideImg: config.recommendationsWideImages,
        link: config.link,
        gr: config.globalRecommendations,
        hideCommentBoxWithButton: config.comments && config.comments.hideCommentInputBoxWithButton,
        wpSync: config.wordpressSync,
        disableButtons: config.comments && config.comments.disableButtons,
        isInIframe: true,

        hasAnonFallback: Boolean(TokenHandler.getAnonToken()),

        isCustomText: Boolean(config.customTranslationUrl),
        version: widgetsConfig.versions.comments,
        debug: searchParams.get('vdebug') === 'true',
      },
      false,
      false
    );

    this.intersectionObserverOptions = {
      threshold: 0,
      rootMargin: '400px 400px 400px 400px',
    };
    this.articleIndex = articleIndex;

    this.iframe.allow = 'web-share';
    this.iframe.style.minWidth = '100%';
    this.iframe.style.overflow = 'hidden';
    this.iframe.style.order = '1';
    this.iframe.style.minHeight = advancedKeys[config.apiKey]?.compact ? '50px' : '320px';
    // scheduler.postTask(() => {
    //   // Create blue bar when realtime is enabled and the browser supports required feature
    //   if (realtimeSocket.enabled && typeof DOMRect !== 'undefined') {
    //     this.createRealtimeBar();
    //   }
    // });
    /**
     * Post custom card styles on load
     * Post custom comments listen styles on load
     * ---
     * It's needed because custom styles config object can be very big
     * and we can't simply pass it using query string inside URL
     */
    this.onLoad((iframeWindow) =>
      iframeWindow.postMessage({ cardStyles: config.theme && config.theme.cardStyles }, '*')
    );
    this.onLoad((iframeWindow) =>
      iframeWindow.postMessage({ listenButtonStyles: config.theme && config.theme.listenButtonStyles }, '*')
    );
    /**
     * ↕ Scroll to comment if hash value in URL exists.
     * Articles can have hash in their links with the comment ID value. If it's there we need to tell comments widget
     * to load comment that comment ID on top of the feed and then scroll to the comments widget, so user will be directly
     * navigated to the comment.
     * @example example.com/#commentID-1234, example.com/article-1#commentID-4321
     */
    if (window.location.hash && window.location.hash.match(/commentID/)) {
      const commentID = window.location.hash.split('-')[1];
      this.onLoad((iframeWindow) => {
        /** Tell widget for what ID to load details */
        iframeWindow.postMessage({ loadComment: commentID }, '*');
        /**
         * We need to scroll to comments widget from this callback, so we might suppose
         * that many things on the page are loaded and length of document won't change, so in result scroll to function
         * will be most accurate
         */

        // In some websites platform is lazily inserted after DOM finishes loading which is why we'll scroll without an event listener
        if (document.readyState === 'interactive' || document.readyState == 'complete') {
          setTimeout(() => {
            const iframeRect = this.iframe.getBoundingClientRect();
            window.scrollTo(iframeRect.x, iframeRect.y);
            // this.iframe.scrollIntoView();
          }, 1500);
        } else {
          const iframe = this.iframe;
          /**
           *
           */
          function onReadyStateChange() {
            if (document.readyState === 'interactive' || document.readyState === 'complete') {
              setTimeout(() => {
                const iframeRect = iframe.getBoundingClientRect();
                // Use scrollTo here to prevent scroll anchoring: https://developers.google.com/web/updates/2016/04/scroll-anchoring
                window.scrollTo(iframeRect.x, iframeRect.y);
                // iframe.scrollIntoView();
                document.removeEventListener('readystatechange', onReadyStateChange);
              }, 1500);
            }
          }
          document.addEventListener('readystatechange', onReadyStateChange);
        }
      });
      this.loadIframe();
    }

    const serverScrollLogger = this.serverScrollLogger(config.amp || false);
    // const realtimeLogger = this.realtimeViewLogger(realtimeSocket); TEMPORARY removed
    if (typeof IntersectionObserver !== 'undefined') {
      const commentsObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            const { boundingClientRect, intersectionRatio, isIntersecting } = entry;
            const isInView = intersectionRatio > 0.025 || isIntersecting;
            // realtimeLogger(); TEMPORARY removed
            scheduler.postTask(
              () => {
                serverScrollLogger(isInView);
              },
              { priority: 'background' }
            );
            // If the user scrolled close to the 'Load New Comments button'
            if (isInView && this.realtimeBar && boundingClientRect.top >= -300) {
              this.realtimeBar.style.display = 'none';
            }
          });
        },
        { threshold: 0.1 }
      );
      commentsObserver.observe(this.iframe);
    } else {
      window.addEventListener(
        'scroll',
        () => {
          // realtimeLogger(); TEMPORARY removed
          scheduler.postTask(
            () => {
              serverScrollLogger(this.isInView);
            },
            { priority: 'background' }
          );
          // If the user scrolled close to the 'Load New Comments button'
          if (this.isInView && this.realtimeBar && this.iframe.getBoundingClientRect().top >= -300) {
            this.realtimeBar.style.display = 'none';
          }
        },
        false
      );
    }
  }

  private bubbleOperationCallbacks = new Set<() => void>();
  private persistBubble = false;

  public changeMinHeight(minHeight: number) {
    this.iframe.style.minHeight = `${minHeight}px`;
  }

  // protected insertSkeleton(): void {
  //   super.insertSkeleton();
  //   const baseSkeletonPiece = document.createElement('span');
  //   baseSkeletonPiece.style.cssText = `
  //     display: block;
  //     background: linear-gradient(
  //         to right,
  //         rgba(255, 255, 255, 0),
  //         rgba(255, 255, 255, 0.5) 50%,
  //         rgba(255, 255, 255, 0) 80%
  //       ),
  //       lightgray;
  //     background-repeat: repeat-y;
  //     background-size: 50px 500px;
  //     background-position: 0 0;
  //     animation: iframe-skeleton-animation 1s infinite;
  //   `;

  //   const headerPieces = document.createElement('div');
  //   headerPieces.style.cssText = `
  //     display: flex;
  //     justify-content: space-between;
  //     align-items: center;
  //     margin-bottom: 10px;
  //   `;

  //   const firstHeaderPiece = baseSkeletonPiece.cloneNode() as HTMLSpanElement;
  //   firstHeaderPiece.style.width = '200px';
  //   firstHeaderPiece.style.height = '32px';

  //   const secondHeaderPiece = baseSkeletonPiece.cloneNode() as HTMLSpanElement;
  //   secondHeaderPiece.style.width = '113px';
  //   secondHeaderPiece.style.height = '32px';

  //   headerPieces.appendChild(firstHeaderPiece);
  //   headerPieces.appendChild(secondHeaderPiece);

  //   const commentBoxPiece = baseSkeletonPiece.cloneNode() as HTMLSpanElement;
  //   commentBoxPiece.style.width = '100%';
  //   commentBoxPiece.style.height = '107px';
  //   commentBoxPiece.style.borderRadius = '4px';

  //   this.iframeSkeleton.appendChild(headerPieces);
  //   this.iframeSkeleton.appendChild(commentBoxPiece);
  // }

  public createScrollListenerForCommentBubble(
    commentCount: number,
    shouldWaitForTimeBeforeShowingBubble: boolean,
    waitTime: number,
    percentageToScrollBeforeShowingBubble: number
  ) {
    const bubbleComponents = this.createCommentBubble(commentCount);
    if (bubbleComponents) {
      let oldScrollPosition = 0;
      const scrollPercentage = percentageToScrollBeforeShowingBubble / 100;
      const { bubble, bubbleMenu, backButton, backMenu, containerButton } = bubbleComponents;
      const bubbleOperationCallbacks = this.bubbleOperationCallbacks;
      containerButton.addEventListener('click', (e) => {
        e.preventDefault();
        oldScrollPosition = document.documentElement.scrollTop;

        // Log bq event
        scheduler.postTask(
          () => {
            ServerLogger.log('CWbubbleClick', this.config);
          },
          { priority: 'background' }
        );

        // Not all browsers support this version of scrollIntoView() unfortunately
        this.iframe.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        });
        backMenu.style.display = 'flex';
        bubbleMenu.style.display = 'none';
        bubble.style.display = 'none';
      });
      backButton.addEventListener('click', () => {
        // Take user back to their previous scroll position when they clicked on the comment bubble button
        document.documentElement.scrollTop = oldScrollPosition;
        backMenu.style.display = 'none';
        bubbleMenu.style.display = 'none';
        bubble.style.display = 'flex';
      });
      const commentsIframe = this;
      let hasScrolledEnoughForFirstAppearance = false;
      let canShowBubble = false;
      /** Show CW bubble with comment count if the user has scrolled over 30% of the page */
      function showCommentBubble() {
        const scrollY = window.scrollY;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;
        const totalScrolled = scrollY / (scrollHeight - clientHeight);
        // Hide/Show the bubble or the back button depending on what the user's scroll position is

        if (canShowBubble) {
          if (commentsIframe.isInView) {
            backMenu.style.display = 'flex';
            bubbleMenu.style.display = 'none';
            bubble.style.display = 'none';
          } else {
            backMenu.style.display = 'none';
            bubble.style.display = 'flex';
          }
        }

        if (
          totalScrolled >= scrollPercentage &&
          !shouldWaitForTimeBeforeShowingBubble &&
          !hasScrolledEnoughForFirstAppearance
        ) {
          canShowBubble = true;
          bubble.style.display = 'flex';
          hasScrolledEnoughForFirstAppearance = true;
          bubbleOperationCallbacks.forEach((cb) => cb());
          setTimeout(() => {
            if (!commentsIframe.persistBubble) {
              window.removeEventListener('scroll', showCommentBubble);
              bubble?.remove();
              bubbleMenu?.remove();
              backMenu?.remove();
            }
          }, 10000);
        }
      }
      if (!commentBubbleExcludeKeys.includes(this.config.apiKey)) {
        window.addEventListener('scroll', showCommentBubble);
      }
      if (shouldWaitForTimeBeforeShowingBubble) {
        setTimeout(() => {
          canShowBubble = true;
          bubble.style.display = 'flex';
          bubbleOperationCallbacks.forEach((cb) => cb());
          setTimeout(() => {
            if (!this.persistBubble) {
              window.removeEventListener('scroll', showCommentBubble);
              bubble?.remove();
              bubbleMenu?.remove();
              backMenu?.remove();
            }
          }, 10000);
        }, waitTime * 1000);
      }
    }
  }

  private createCommentBubble(commentCount: number) {
    // Prevent duplicate bubble creation
    if (document.querySelector(`#vuukle-comments-bubble-${this.id}`)) {
      return;
    }
    const bubble = document.createElement('div');
    const counter = document.createElement('span');
    const menuThreeDots = document.createElement('button');
    const containerButton = document.createElement('button');
    const downArrowIcon = document.createElement('span');
    const bubbleMenu = document.createElement('div');
    const hideButton = document.createElement('button');
    const backMenu = document.createElement('div');
    const backButton = document.createElement('button');
    // const commentIcon = document.createElement('svg');

    bubble.className = 'vuukle-comments-bubble';
    bubble.style.cssText = `
      position: fixed;
      display: none;
      align-items: center;
      justify-content: center;
      left: 50%;
      transform: translate(-50%, 0);
      bottom: 100px;
      width: 128px;
      height: 40px;
      background: #fff;
      color: #000;
      border: 1px solid #c1c1c1;
      border-radius: 6px;
      box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.15);
      text-align: center;
      transition: all 0.5s;
      text-decoration: none;
      z-index: 100000;
    `;

    bubbleMenu.className = 'vuukle-comments-bubble-menu';
    bubbleMenu.style.cssText = `
      position: fixed;
      display: none;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      left: 50%;
      transform: translate(-50%, 0);
      bottom: 160px;
      width: 128px;
      height: 40px;
      background: #fff;
      color: #000;
      border: 1px solid #c1c1c1;
      border-radius: 6px;
      box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.15);
      text-align: center;
      transition: all 0.5s;
      text-decoration: none;
      z-index: 100000;
  `;

    backMenu.className = 'vuukle-comments-bubble-back-button-container';
    backMenu.style.cssText = `
      position: fixed;
      display: none;
      align-items: center;
      justify-content: center;
      left: 50%;
      transform: translate(-50%, 0);
      bottom: 100px;
      width: 128px;
      height: 40px;
      background: #fff;
      color: #000;
      border: 1px solid #c1c1c1;
      border-radius: 6px;
      box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.15);
      text-align: center;
      transition: all 0.5s;
      text-decoration: none;
      z-index: 100000;    
    `;

    // commentIcon.style.cssText = `
    //   width: 24px;
    //   height: 24px;
    //   fill: #4885ED;
    //   vertical-align: middle;
    // `;

    counter.style.cssText = `
      color: #000;
      line-height: 25px;
      text-align: center;
      padding: 0px 5px;
      font-size: 13px;
      font-weight: 400;
      z-index: 100000;
    `;

    menuThreeDots.className = 'vuukle-comments-bubble-three-dots';
    menuThreeDots.style.cssText = `
      background: transparent;
      border: none;
      cursor: pointer;
      border-left: 1px solid #ccc;
      padding-left: 10px;
      display: ${Session.isMobileUser ? 'block' : 'none'};
      opacity: ${Session.isMobileUser ? '1' : '0'};
      animation: three-dots-fade-in 0.5s;
    `;

    containerButton.className = 'vuukle-comments-bubble-container-button';
    containerButton.style.cssText = `
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px;
    `;

    hideButton.style.cssText = `
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      justify-content: center;
    `;

    backButton.style.cssText = `
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;
      width: 100%;
      justify-content: center;
    `;

    backButton.innerHTML = `
      <span>Back</span>
      <svg fill="#000000" width="14px" height="14px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><line x1="12" y1="21" x2="12" y2="3" style="fill: none; stroke: rgb(0, 0, 0); stroke-linecap: round; stroke-linejoin: round; stroke-width: 2;"></line><polyline points="15 6 12 3 9 6" style="fill: none; stroke: rgb(0, 0, 0); stroke-linecap: round; stroke-linejoin: round; stroke-width: 2;"></polyline></svg>
    `;

    hideButton.innerHTML = `
      <svg width="24px" height="24px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M876.8 156.8c0-9.6-3.2-16-9.6-22.4-6.4-6.4-12.8-9.6-22.4-9.6-9.6 0-16 3.2-22.4 9.6L736 220.8c-64-32-137.6-51.2-224-60.8-160 16-288 73.6-377.6 176C44.8 438.4 0 496 0 512s48 73.6 134.4 176c22.4 25.6 44.8 48 73.6 67.2l-86.4 89.6c-6.4 6.4-9.6 12.8-9.6 22.4 0 9.6 3.2 16 9.6 22.4 6.4 6.4 12.8 9.6 22.4 9.6 9.6 0 16-3.2 22.4-9.6l704-710.4c3.2-6.4 6.4-12.8 6.4-22.4Zm-646.4 528c-76.8-70.4-128-128-153.6-172.8 28.8-48 80-105.6 153.6-172.8C304 272 400 230.4 512 224c64 3.2 124.8 19.2 176 44.8l-54.4 54.4C598.4 300.8 560 288 512 288c-64 0-115.2 22.4-160 64s-64 96-64 160c0 48 12.8 89.6 35.2 124.8L256 707.2c-9.6-6.4-19.2-16-25.6-22.4Zm140.8-96c-12.8-22.4-19.2-48-19.2-76.8 0-44.8 16-83.2 48-112 32-28.8 67.2-48 112-48 28.8 0 54.4 6.4 73.6 19.2L371.2 588.8ZM889.599 336c-12.8-16-28.8-28.8-41.6-41.6l-48 48c73.6 67.2 124.8 124.8 150.4 169.6-28.8 48-80 105.6-153.6 172.8-73.6 67.2-172.8 108.8-284.8 115.2-51.2-3.2-99.2-12.8-140.8-28.8l-48 48c57.6 22.4 118.4 38.4 188.8 44.8 160-16 288-73.6 377.6-176C979.199 585.6 1024 528 1024 512s-48.001-73.6-134.401-176Z" fill="#000000"/><path d="M511.998 672c-12.8 0-25.6-3.2-38.4-6.4l-51.2 51.2c28.8 12.8 57.6 19.2 89.6 19.2 64 0 115.2-22.4 160-64 41.6-41.6 64-96 64-160 0-32-6.4-64-19.2-89.6l-51.2 51.2c3.2 12.8 6.4 25.6 6.4 38.4 0 44.8-16 83.2-48 112-32 28.8-67.2 48-112 48Z" fill="#000"/></svg>
      <span>Hide</span>
    `;

    menuThreeDots.innerHTML = `
      <svg height="20px" width="20px" fill="#ccc" xmlns="http://www.w3.org/2000/svg" viewbox="0 0 16 16">
        <path d="M8,6.5A1.5,1.5,0,1,1,6.5,8,1.5,1.5,0,0,1,8,6.5ZM.5,8A1.5,1.5,0,1,0,2,6.5,1.5,1.5,0,0,0,.5,8Zm12,0A1.5,1.5,0,1,0,14,6.5,1.5,1.5,0,0,0,12.5,8Z"/>
      </svg>
    `;

    downArrowIcon.innerHTML = `
      <svg fill="#000" width="14px" height="14px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <line x1="12" y1="3" x2="12" y2="21" style="fill: none; stroke: rgb(0, 0, 0); stroke-linecap: round; stroke-linejoin: round; stroke-width: 2;"></line>
        <polyline points="9 18 12 21 15 18" style="fill: none; stroke: rgb(0, 0, 0); stroke-linecap: round; stroke-linejoin: round; stroke-width: 2;"></polyline>
      </svg>
    `;

    containerButton.insertAdjacentHTML(
      'afterbegin',
      `<svg style="cursor: pointer; width: 24px; height: 24px; fill: #4885ED; vertical-align: middle; display: block;" viewbox="0 0 24 24"><path d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22V22H9Z"></path></svg>`
    );
    bubble.id = `vuukle-comments-bubble-${this.id}`;

    const bubbleStyles = document.createElement('style');
    bubbleStyles.id = `vuukle-comments-bubble-styles-${this.id}`;

    bubbleStyles.innerHTML = `
      .vuukle-comments-bubble:hover .vuukle-comments-bubble-three-dots {
        display: flex !important;
        opacity: 1 !important;
      }

      @keyframes three-dots-fade-in {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
    `;

    hideButton.addEventListener('click', () => {
      bubble.remove();
      bubbleStyles.remove();
      bubbleMenu.remove();
      backMenu.remove();
    });

    menuThreeDots.addEventListener('click', (e) => {
      e.preventDefault();
      if (bubbleMenu.style.display === 'none') {
        bubbleMenu.style.display = 'flex';
      } else {
        bubbleMenu.style.display = 'none';
      }
    });

    counter.innerHTML = commentCount === 0 ? '' : commentCount.toString();
    containerButton.appendChild(counter);
    containerButton.appendChild(downArrowIcon);
    bubble.appendChild(containerButton);
    bubble.appendChild(menuThreeDots);
    bubbleMenu.appendChild(hideButton);
    backMenu.appendChild(backButton);
    document.body.appendChild(bubble);
    document.body.appendChild(bubbleStyles);
    document.body.appendChild(bubbleMenu);
    document.body.appendChild(backMenu);

    return { bubble, bubbleMenu, containerButton, backMenu, backButton };
  }

  public changeBubblePosition(position: 'left' | 'right') {
    this.bubbleOperationCallbacks.add(() => {
      const bubble = document.getElementById(`vuukle-comments-bubble-${this.id}`);
      if (bubble) {
        if (position === 'left') {
          bubble.style.removeProperty('right');
          bubble.style.left = '20px';
        } else {
          bubble.style.removeProperty('left');
          bubble.style.right = '20px';
        }
      }
    });
  }

  public makeBubblePersistent(isPersistent: boolean) {
    this.persistBubble = isPersistent;
  }

  /** Add message listener to realtime to share messages with comments widget  */
  // public addRealtimeListener() {
  //   this.realtimeSocket.addMessageListener((message: any) => {
  //     try {
  //       const newData = JSON.parse(message.data);
  //       // Send only needed messages to don't waste resources
  //       if (newData.currentCommentCount || newData.usersWriting) {
  //         this.onLoad((iframeWindow) => iframeWindow.postMessage({ realtimeResponse: newData }, '*'));
  //         // If the initial comment count is fetched
  //         if (
  //           newData.currentCommentCount > this.commentCount &&
  //           this.realtimeBar &&
  //           !CommunicationCreator.commentCountLock
  //         ) {
  //           this.commentCount = newData.currentCommentCount;
  //           CommunicationCreator.commentCountLock = true;
  //           return;
  //         }
  //         if (CommunicationCreator.commentCountLock && this.realtimeBar) {
  //           // If the user scrolled down deeper than the comment input box is visible
  //           if (!this.isCommentInputBoxInView) {
  //             // Subtract the user's own posted comments from the 'load new message' count
  //             const actualNewComments: number =
  //               newData.currentCommentCount - this.commentCount - CommunicationCreator.postedCommentCount;
  //             const moreThanOne: boolean = actualNewComments > 1;
  //             if (this.commentCount !== 0 && actualNewComments > 0) {
  //               // Show the top bar with the new comment number if it's a positive value
  //               this.realtimeBar.style.display = 'block';
  //               this.realtimeBar.textContent = `Load ${actualNewComments} new message${moreThanOne ? 's' : ''}`;
  //             } else {
  //               // Don't show blue bar if the new comment value is 0 or negative
  //               this.realtimeBar.textContent = '';
  //               this.realtimeBar.style.display = 'none';
  //             }
  //           }
  //         }
  //       }
  //     } catch (err) {
  //       debugLog('warn', 'Realtime Message error', err);
  //     }
  //   });
  // }

  /** Realtime view logger creator for scroll listener */
  // private realtimeViewLogger = (realtimeObj: RealtimeAnalytics) => {
  //   // TODO: mb we need to improve it or replace with more logical solution
  //   let isInViewSent = false;
  //   return debounce(() => {
  //     if (this.isInView && !isInViewSent) {
  //       realtimeObj.send({ type: RealtimeMessageTypes.StartCommentsReading });
  //       isInViewSent = true;
  //     } else if (!this.isInView && isInViewSent) {
  //       realtimeObj.send({ type: RealtimeMessageTypes.StopCommentsReading });
  //       isInViewSent = false;
  //     }
  //   }, 500);
  // };

  /** Blue bar that appears after there are new comments available by realtime */
  private realtimeBar: HTMLDivElement | null = null;
  /** Comment count before the user clicked on 'load new comments' */
  public commentCount: number = 0;

  get isCommentInputBoxInView(): boolean {
    const iframeRect = this.iframe.getBoundingClientRect();
    if (CommunicationCreator.commentInputBoxRect) {
      const commentInputBoxRect = CommunicationCreator.commentInputBoxRect;
      const rect = new DOMRect(
        iframeRect.left + commentInputBoxRect.left,
        iframeRect.top + commentInputBoxRect.top,
        commentInputBoxRect.width,
        commentInputBoxRect.height
      );
      return rect.bottom > 0 && rect.right > 0 && rect.left < window.innerWidth && rect.bottom < window.innerHeight;
    } else {
      return (
        iframeRect.bottom > 0 &&
        iframeRect.right > 0 &&
        iframeRect.left < window.innerWidth &&
        iframeRect.top < window.innerHeight
      );
    }
  }

  /** Create a fixed position top bar for realtime notifications about messages */
  public createRealtimeBar = () => {
    // let commentsRect = document.body.getBoundingClientRect();
    // const commentsWidgetWrapper = document.querySelector(widgetsConfig.selectors.comments.concat(this.articleIndex));
    // if (commentsWidgetWrapper instanceof HTMLDivElement) {
    //   commentsRect = commentsWidgetWrapper.getBoundingClientRect();
    // }

    // const realtimeBarWidth = `${(commentsRect.width / document.body.clientWidth / 2) * 100}%`;
    // const realtimeBarLeft = `${((commentsRect.left + commentsRect.width / 4) / document.body.clientWidth) * 100}%`;

    if (!document.querySelector('#vuukle-comments-realtime-bar'.concat(this.articleIndex))) {
      this.realtimeBar = document.createElement('div');
      this.realtimeBar.style.cssText = `
        // position: fixed;
        // top: 0;
        // left: 0;
        width: 100%;
        height: 25px;
        background: #3997e9;
        color: #fff;
        cursor: pointer;
        font-size: 15px;
        font-family: -apple-system,system-ui,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',sans-serif;
        font-weight: 500;
        text-align: center;
        // z-index: 9999;
        display: none;
      `;
      this.realtimeBar.id = 'vuukle-comments-realtime-bar'.concat(this.articleIndex);
      this.realtimeBar.textContent = '';
      this.realtimeBar.onclick = () => {
        // Scroll to the top of comments Iframe and then reset self
        this.iframe.scrollIntoView();
        if (this.realtimeBar) {
          this.realtimeBar.style.display = 'none';
          this.realtimeBar.textContent = '';
          CommunicationCreator.commentCountLock = false;
        }
        // Tell the comments widget to load new messages
        this.onLoad((iframeWindow) => iframeWindow.postMessage({ loadNewRealtime: true }, '*'));
      };
      // Put the bar inside DOM's body as one of the first elements
      document.body.insertAdjacentElement('afterbegin', this.realtimeBar);
    }
  };

  private serverScrollLogger = (isAMPArticle: boolean) => {
    // We need to log message instantly as we don't have access to JS things inside AMP articles
    if (isAMPArticle) {
      ServerLogger.log('view_comments', this.config);
      this.onLoad((iframeWindow) => iframeWindow.postMessage({ isViewed: true }, '*'));
      return () => false;
    }
    // Otherwise create listener to sent logs only when needed
    let viewLogSent = false;
    return debounce((inView: boolean) => {
      const inViewNoLogSent = inView && !viewLogSent;

      if (inViewNoLogSent) {
        ServerLogger.log('view_comments', this.config);
        this.onLoad((iframeWindow) => iframeWindow.postMessage({ isViewed: true }, '*'));
        viewLogSent = true;
      }
    }, 300);
  };
}

export default Comments;
